import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Typography,
  Space,
  Tag,
  Button,
  Select,
  DatePicker,
  Input,
  Badge,
  Tooltip,
  Progress,
  Statistic,
  Divider,
  Alert,
  Modal,
  Form,
  Switch,
  message,
  List,
  Avatar
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  DollarOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  TeamOutlined,
  CrownOutlined,
  BankOutlined,
  TrophyOutlined,
  LinkOutlined,
  StarOutlined,
  PercentageOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axiosConfig from '../../../../../config/axiosConfig';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Search } = Input;

const PartnerAnalyticsTable = ({ visible, onClose, filterType, data, loading, onNavigate }) => {
  const [partners, setPartners] = useState([]);
  const [filteredPartners, setFilteredPartners] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [dateRange, setDateRange] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  // Mock partner data - replace with actual API call
  const mockPartners = [
    {
      id: 1,
      partnerName: 'Digital Solutions Inc',
      contactPerson: 'Alex Thompson',
      email: 'alex@digitalsolutions.com',
      phone: '+1-555-0201',
      status: 'Active',
      tier: 'Gold',
      joinDate: '2024-01-10',
      lastActive: '2024-03-15',
      customersReferred: 15,
      activeCustomers: 12,
      inactiveCustomers: 3,
      totalRevenue: 23456.78,
      monthlyRevenue: 4567.89,
      commission: 2345.67,
      commissionRate: 10,
      conversionRate: 80,
      averageCustomerValue: 1563.78,
      totalMessages: 234567,
      monthlyMessages: 45678,
      supportTickets: 3,
      rating: 4.8,
      certificationLevel: 'Advanced',
      territoryFocus: 'North America',
      specialization: ['Healthcare', 'E-commerce'],
      performanceScore: 92,
      billingStatus: 'Paid',
      nextPaymentDate: '2024-04-10'
    },
    {
      id: 2,
      partnerName: 'Marketing Pro Agency',
      contactPerson: 'Sarah Williams',
      email: 'sarah@marketingpro.com',
      phone: '+1-555-0202',
      status: 'Active',
      tier: 'Silver',
      joinDate: '2024-02-05',
      lastActive: '2024-03-14',
      customersReferred: 8,
      activeCustomers: 7,
      inactiveCustomers: 1,
      totalRevenue: 12345.67,
      monthlyRevenue: 2456.78,
      commission: 1234.56,
      commissionRate: 10,
      conversionRate: 87.5,
      averageCustomerValue: 1543.21,
      totalMessages: 123456,
      monthlyMessages: 23456,
      supportTickets: 1,
      rating: 4.6,
      certificationLevel: 'Intermediate',
      territoryFocus: 'Europe',
      specialization: ['Retail', 'SaaS'],
      performanceScore: 88,
      billingStatus: 'Paid',
      nextPaymentDate: '2024-04-05'
    },
    {
      id: 3,
      partnerName: 'TechConnect Solutions',
      contactPerson: 'Michael Davis',
      email: 'michael@techconnect.com',
      phone: '+1-555-0203',
      status: 'Pending',
      tier: 'Bronze',
      joinDate: '2024-03-01',
      lastActive: '2024-03-10',
      customersReferred: 3,
      activeCustomers: 2,
      inactiveCustomers: 1,
      totalRevenue: 4567.89,
      monthlyRevenue: 1234.56,
      commission: 456.78,
      commissionRate: 10,
      conversionRate: 66.7,
      averageCustomerValue: 1522.63,
      totalMessages: 34567,
      monthlyMessages: 12345,
      supportTickets: 5,
      rating: 4.2,
      certificationLevel: 'Basic',
      territoryFocus: 'Asia Pacific',
      specialization: ['Manufacturing', 'Logistics'],
      performanceScore: 75,
      billingStatus: 'Pending',
      nextPaymentDate: '2024-04-01'
    },
    {
      id: 4,
      partnerName: 'Global Comm Partners',
      contactPerson: 'Lisa Zhang',
      email: 'lisa@globalcomm.com',
      phone: '+1-555-0204',
      status: 'Active',
      tier: 'Platinum',
      joinDate: '2023-11-15',
      lastActive: '2024-03-15',
      customersReferred: 25,
      activeCustomers: 22,
      inactiveCustomers: 3,
      totalRevenue: 45678.90,
      monthlyRevenue: 8901.23,
      commission: 4567.89,
      commissionRate: 10,
      conversionRate: 88,
      averageCustomerValue: 1827.16,
      totalMessages: 456789,
      monthlyMessages: 78901,
      supportTickets: 2,
      rating: 4.9,
      certificationLevel: 'Expert',
      territoryFocus: 'Global',
      specialization: ['Enterprise', 'Financial Services', 'Healthcare'],
      performanceScore: 96,
      billingStatus: 'Paid',
      nextPaymentDate: '2024-04-15'
    },
    {
      id: 5,
      partnerName: 'StartupBoost Ltd',
      contactPerson: 'James Kumar',
      email: 'james@startupboost.com',
      phone: '+1-555-0205',
      status: 'Inactive',
      tier: 'Bronze',
      joinDate: '2023-12-20',
      lastActive: '2024-02-20',
      customersReferred: 2,
      activeCustomers: 0,
      inactiveCustomers: 2,
      totalRevenue: 1234.56,
      monthlyRevenue: 0,
      commission: 123.45,
      commissionRate: 10,
      conversionRate: 0,
      averageCustomerValue: 617.28,
      totalMessages: 12345,
      monthlyMessages: 0,
      supportTickets: 8,
      rating: 3.8,
      certificationLevel: 'Basic',
      territoryFocus: 'South America',
      specialization: ['Startups', 'Small Business'],
      performanceScore: 45,
      billingStatus: 'Suspended',
      nextPaymentDate: '2024-03-20'
    }
  ];

  useEffect(() => {
    if (data && data.companies) {
      setPartners(data.companies);
      setPagination(prev => ({ ...prev, total: data.totalrecords || data.companies.length }));
    } else {
      setPartners([]);
    }
  }, [data]);

  useEffect(() => {
    filterPartners();
  }, [partners, searchText, statusFilter, tierFilter, dateRange]);

  const filterPartners = () => {
    let filtered = [...partners];

    if (searchText) {
      filtered = filtered.filter(partner =>
        (partner.companyname || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (partner.emailid || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (partner.contactno || '').toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(partner => {
        const status = partner.status === 1 ? 'active' : 'inactive';
        return status === statusFilter;
      });
    }

    if (tierFilter !== 'all') {
      // Map tier based on customer count
      filtered = filtered.filter(partner => {
        const customerCount = partner.totalcustomer || 0;
        let tier = 'bronze';
        if (customerCount >= 20) tier = 'platinum';
        else if (customerCount >= 15) tier = 'gold';
        else if (customerCount >= 10) tier = 'silver';
        return tier === tierFilter;
      });
    }

    if (dateRange && dateRange.length === 2) {
      filtered = filtered.filter(partner => {
        const joinDate = moment(partner.createtime);
        return joinDate.isBetween(dateRange[0], dateRange[1], 'day', '[]');
      });
    }

    setFilteredPartners(filtered);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active': return 'green';
      case 'inactive': return 'red';
      case 'pending': return 'orange';
      case 'suspended': return 'purple';
      default: return 'default';
    }
  };

  const getTierColor = (tier) => {
    switch (tier.toLowerCase()) {
      case 'platinum': return '#722ed1';
      case 'gold': return '#faad14';
      case 'silver': return '#8c8c8c';
      case 'bronze': return '#d4734b';
      default: return 'default';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier.toLowerCase()) {
      case 'platinum': return <CrownOutlined style={{ color: '#722ed1' }} />;
      case 'gold': return <TrophyOutlined style={{ color: '#faad14' }} />;
      case 'silver': return <StarOutlined style={{ color: '#8c8c8c' }} />;
      case 'bronze': return <StarOutlined style={{ color: '#d4734b' }} />;
      default: return <StarOutlined />;
    }
  };

  const getPerformanceColor = (score) => {
    if (score >= 90) return '#52c41a';
    if (score >= 80) return '#1890ff';
    if (score >= 70) return '#faad14';
    if (score >= 60) return '#fa8c16';
    return '#ff4d4f';
  };

  const columns = [
    {
      title: 'Partner Details',
      key: 'partner',
      width: 280,
      render: (_, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <Avatar
              size="large"
              style={{ backgroundColor: getTierColor(record.tier), marginRight: 12 }}
              icon={getTierIcon(record.tier)}
            />
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: 2 }}>
                {record.partnerName}
              </div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#666', lineHeight: '18px' }}>
            <div><UserOutlined /> {record.contactPerson}</div>
            <div><MailOutlined /> {record.email}</div>
            <div><PhoneOutlined /> {record.phone}</div>
          </div>
        </div>
      ),
    },
    {
      title: 'Status & Quality',
      key: 'status',
      width: 150,
      render: (_, record) => (
        <div>
          <Tag color={getStatusColor(record.status)} style={{ marginBottom: 8 }}>
            {record.status}
          </Tag>
          <div style={{ fontSize: '11px', marginTop: 8 }}>
            <Text>Quality Rating: </Text>
            <Tag color="green" size="small">
              Good
            </Tag>
          </div>
        </div>
      ),
    },
    {
      title: 'Customer Metrics',
      key: 'customers',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#1890ff' }}>
            {record.customersReferred}
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            Total Referred
          </div>
          <div style={{ fontSize: '11px', lineHeight: '16px' }}>
            <div style={{ color: '#52c41a' }}>
              <CheckCircleOutlined /> {record.activeCustomers} active
            </div>
            <div style={{ color: '#ff4d4f' }}>
              <CloseCircleOutlined /> {record.inactiveCustomers} inactive
            </div>
          </div>
        </div>
      ),
      sorter: (a, b) => a.customersReferred - b.customersReferred,
    },
    {
      title: 'Revenue & Commission',
      key: 'revenue',
      width: 180,
      render: (_, record) => (
        <div>
          <div>
            <Text style={{ fontSize: '11px', color: '#666' }}>Total Revenue</Text>
            <div style={{ fontWeight: 'bold', color: '#52c41a', fontSize: '14px' }}>
              ${record.totalRevenue.toLocaleString()}
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <Text style={{ fontSize: '11px', color: '#666' }}>Monthly Revenue</Text>
            <div style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '14px' }}>
              ${record.monthlyRevenue.toLocaleString()}
            </div>
          </div>
          <Divider style={{ margin: '8px 0' }} />
          <div>
            <Text style={{ fontSize: '11px', color: '#666' }}>Commission ({record.commissionRate}%)</Text>
            <div style={{ fontWeight: 'bold', color: '#722ed1', fontSize: '14px' }}>
              ${record.commission.toLocaleString()}
            </div>
          </div>
          <div style={{ fontSize: '11px', color: '#666', marginTop: 4 }}>
            Avg Customer Value: ${record.averageCustomerValue.toFixed(0)}
          </div>
        </div>
      ),
      sorter: (a, b) => a.totalRevenue - b.totalRevenue,
    },
    {
      title: 'Messages & Activity',
      key: 'activity',
      width: 150,
      render: (_, record) => (
        <div>
          <div>
            <Text style={{ fontSize: '11px', color: '#666' }}>Total Messages</Text>
            <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
              {record.totalMessages.toLocaleString()}
            </div>
          </div>
          <div style={{ marginTop: 8 }}>
            <Text style={{ fontSize: '11px', color: '#666' }}>This Month</Text>
            <div style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '14px' }}>
              {record.monthlyMessages.toLocaleString()}
            </div>
          </div>
          <div style={{ fontSize: '11px', marginTop: 4 }}>
            <Text>Support Tickets: </Text>
            <Text style={{ 
              color: record.supportTickets <= 2 ? '#52c41a' : 
                     record.supportTickets <= 5 ? '#faad14' : '#ff4d4f'
            }}>
              {record.supportTickets}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Territory & Focus',
      key: 'territory',
      width: 160,
      render: (_, record) => (
        <div>
          <div style={{ marginBottom: 8 }}>
            <Text style={{ fontSize: '11px', color: '#666' }}>Territory</Text>
            <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
              {record.territoryFocus}
            </div>
          </div>
          <div>
            <Text style={{ fontSize: '11px', color: '#666', marginBottom: 4 }}>
              Specialization
            </Text>
            <div>
              {record.specialization.map((spec, index) => (
                <Tag key={index} size="small" style={{ fontSize: '10px', marginBottom: 2 }}>
                  {spec}
                </Tag>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Billing',
      key: 'billing',
      width: 120,
      render: (_, record) => (
        <div>
          <Tag color={record.billingStatus === 'Paid' ? 'green' : 
                     record.billingStatus === 'Pending' ? 'orange' : 'red'}>
            {record.billingStatus}
          </Tag>
          <div style={{ fontSize: '11px', color: '#666', marginTop: 8 }}>
            Next Payment:
          </div>
          <div style={{ fontSize: '11px', fontWeight: 'bold' }}>
            {moment(record.nextPaymentDate).format('MMM DD, YYYY')}
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewPartner(record)}
            style={{ width: '100%', textAlign: 'left' }}
          >
            View Details
          </Button>
          <Button
            type="text"
            size="small"
            icon={<MessageOutlined />}
            onClick={() => handleContactPartner(record)}
            style={{ width: '100%', textAlign: 'left' }}
          >
            Contact
          </Button>
          <Button
            type="text"
            size="small"
            icon={<BankOutlined />}
            onClick={() => handlePayCommission(record)}
            style={{ width: '100%', textAlign: 'left' }}
          >
            Pay Commission
          </Button>
        </Space>
      ),
    },
  ];

  const handleViewPartner = (partner) => {
    onNavigate?.(`partner/${partner.id}`);
  };

  const handleContactPartner = (partner) => {
    console.log('Contact partner:', partner);
  };

  const handlePayCommission = (partner) => {
    console.log('Pay commission to:', partner);
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Solution Partner Analytics - {filterType || 'All Partners'}</span>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => window.location.reload()} size="small">
              Refresh
            </Button>
            <Button icon={<DownloadOutlined />} size="small">
              Export
            </Button>
          </Space>
        </div>
      }
      visible={visible}
      onCancel={onClose}
      width={1600}
      footer={null}
      style={{ top: 20 }}
    >
      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Search
              placeholder="Search partners..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Select
              placeholder="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
              <Option value="pending">Pending</Option>
              <Option value="suspended">Suspended</Option>
            </Select>
          </Col>
          <Col xs={12} sm={4} md={3}>
            <Select
              placeholder="Tier"
              value={tierFilter}
              onChange={setTierFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">All Tiers</Option>
              <Option value="platinum">Platinum</Option>
              <Option value="gold">Gold</Option>
              <Option value="silver">Silver</Option>
              <Option value="bronze">Bronze</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={6}>
          <Statistic
            title="Total Solution Partners"
            value={data?.totalsolutionpartners || filteredPartners.length}
            prefix={<CrownOutlined />}
          />
        </Col>
        <Col xs={6}>
          <Statistic
            title="Active Solution Partners"
            value={filteredPartners.filter(p => p.status === 1).length}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Col>
        <Col xs={6}>
          <Statistic
            title="Total Partner Revenue"
            value={filteredPartners.reduce((sum, p) => sum + ((p.activecustomer || 0) * 50), 0)}
            prefix={<DollarOutlined />}
            precision={2}
            valueStyle={{ color: '#1890ff' }}
          />
        </Col>
        <Col xs={6}>
          <Statistic
            title="Total Commission Earned"
            value={filteredPartners.reduce((sum, p) => sum + ((p.activecustomer || 0) * 5), 0)}
            prefix={<BankOutlined />}
            precision={2}
            valueStyle={{ color: '#722ed1' }}
          />
        </Col>
      </Row>

      {/* Partner Table */}
      <Table
        columns={columns}
        dataSource={filteredPartners}
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} partners`,
        }}
        rowKey="id"
        size="small"
        scroll={{ x: 1400 }}
      />
    </Modal>
  );
};

export default PartnerAnalyticsTable;
